/**
 * All teams (`/teams`): one row per Team the reader sees, with today's
 * numbers.
 *
 *   Team                  its accent dot and name
 *   Backlog               where its tickets come from, the filter as written
 *   Need you … Done       the queue's four groups, off the one list every
 *                         queue reads (`useWorkFeed`), grouped by Team
 *   Today                 how many it picked of its daily cap (`GET /api/desks`)
 *   Gates a person holds  the supervised lines among Propose, Plan and Build
 *   Review                how hard Proctor reviews
 *
 * The last two and the Backlog come from each Team's own lines and
 * attachments, the reads the Team queue makes. A row opens the Team's queue.
 * Below 860px the table scrolls sideways.
 */
import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { DeskAttachment, Job } from "@yagni/shared";

import { deskAccentToken } from "../desks/deskAccent.js";
import { captureUiError } from "../sentry.js";
import { useTeamIdentity } from "../work/useTeamIdentity.js";
import { useWorkFeed } from "../work/useWorkFeed.js";
import { NO_COUNTS, backlogWords, countsByTeam, gatesHeldWords, onAPersonCount, reviewWords } from "./allTeams.js";
import { fetchTeamAttachments, fetchTeamJobs } from "./teamsApi.js";

/** A read not back yet (null), one that failed, or what it said. */
type Read<T> = T | null | "failed";

interface TeamDetail {
  jobs: Read<Job[]>;
  attachments: Read<DeskAttachment[]>;
}

/**
 * Each Team's lines and attachments: the two reads the Team queue makes, once
 * per Team while the page is open. A Team already read is not read again when
 * the roster changes; one whose read failed is.
 */
export function useTeamDetails(ids: readonly string[]): Map<string, TeamDetail> {
  const [details, setDetails] = useState<Map<string, TeamDetail>>(new Map());
  const asked = useRef(new Set<string>());
  // The latest ask per Team: an older ask's late answer never writes over a newer one.
  const latest = useRef(new Map<string, number>());
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const key = ids.join(",");
  useEffect(() => {
    for (const id of ids) {
      if (asked.current.has(id)) continue;
      asked.current.add(id);
      const token = (latest.current.get(id) ?? 0) + 1;
      latest.current.set(id, token);
      const set = (patch: Partial<TeamDetail>) => {
        if (!alive.current || latest.current.get(id) !== token) return;
        setDetails((prev) => {
          const next = new Map(prev);
          next.set(id, { ...(prev.get(id) ?? { jobs: null, attachments: null }), ...patch });
          return next;
        });
      };
      fetchTeamJobs(id)
        .then((body) => set({ jobs: body.engagements.flatMap((group) => group.jobs) }))
        .catch((err: unknown) => {
          captureUiError(err, { area: "Teams", operation: "all_teams_read_jobs", deskId: id, severity: "warning" });
          // A later roster change asks again.
          asked.current.delete(id);
          set({ jobs: "failed" });
        });
      fetchTeamAttachments(id)
        .then((attachments) => set({ attachments }))
        .catch((err: unknown) => {
          captureUiError(err, { area: "Teams", operation: "all_teams_read_attachments", deskId: id, severity: "warning" });
          asked.current.delete(id);
          set({ attachments: "failed" });
        });
    }
    // `key` is the roster's ids; the array itself is new every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return details;
}

/** A cell from a read: blank while it loads, a plain word when it failed. */
function cell<T>(read: Read<T> | undefined, words: (value: T) => ReactNode): ReactNode {
  if (read == null) return "";
  if (read === "failed") return <span title="This read did not answer. Reload to try again.">Could not read</span>;
  return words(read);
}

export function AllTeamsRoute() {
  const navigate = useNavigate();
  const { roster, ready } = useTeamIdentity();
  const feed = useWorkFeed();
  const ids = useMemo(() => roster.map((team) => team.id), [roster]);
  const details = useTeamDetails(ids);

  const counts = useMemo(
    () => countsByTeam(feed.items, ids, Date.now()),
    // The clock moves every render; the counts follow the list.
    [feed.items, ids],
  );

  const open = (id: string) => navigate(`/desks/${encodeURIComponent(id)}`);

  return (
    <div className="teams">
      <div>
        <h1>All teams</h1>
        <p className="intro" style={{ marginTop: 6 }}>
          One row per team. Open a team to work its queue. Numbers are today's.
        </p>
      </div>
      {ready && roster.length === 0 ? (
        <div className="empty dashed tight">
          <p>
            No teams yet. <Link to="/desks/new">Add a team</Link> to give it work.
          </p>
        </div>
      ) : (
        <div className="ttable">
          <table>
            <thead>
              <tr>
                <th>Team</th>
                <th>Backlog</th>
                <th>Need you</th>
                <th>Working</th>
                <th>Stopped</th>
                <th>Done</th>
                <th>Today</th>
                <th>Gates a person holds</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {roster.map((team) => {
                const c = counts.get(team.id) ?? NO_COUNTS;
                const picked = team.pickedToday;
                const detail = details.get(team.id);
                return (
                  <tr
                    key={team.id}
                    tabIndex={0}
                    role="link"
                    onClick={() => open(team.id)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") open(team.id);
                    }}
                    data-testid="all-teams-row"
                  >
                    <td>
                      <span className="tn">
                        <span className="tdot" style={{ "--tc": deskAccentToken(team.id) } as CSSProperties} aria-hidden />
                        {team.name}
                      </span>
                    </td>
                    <td className="mono" data-testid="all-teams-backlog">
                      {cell(detail?.attachments, (attachments) => {
                        const backlog = backlogWords(attachments);
                        return backlog ? (
                          <>
                            {backlog.source}
                            <br />
                            {backlog.filter}
                          </>
                        ) : (
                          "No backlog"
                        );
                      })}
                    </td>
                    <td>
                      <span className="big blue" data-testid="all-teams-needs">
                        {onAPersonCount(c)}
                      </span>
                    </td>
                    <td>
                      <span className="big green" data-testid="all-teams-live">
                        {c.live}
                      </span>
                    </td>
                    <td>
                      <span className="big amber" data-testid="all-teams-stop">
                        {c.stop}
                      </span>
                    </td>
                    <td>
                      <span className="big" data-testid="all-teams-done">
                        {c.done}
                      </span>
                    </td>
                    <td>
                      {picked ? (
                        <>
                          <div className="mono">{picked.cap != null ? `${picked.count} of ${picked.cap}` : `${picked.count} picked`}</div>
                          {picked.cap != null && picked.cap > 0 && (
                            <div className="bar">
                              <i style={{ width: `${Math.min(100, Math.round((100 * picked.count) / picked.cap))}%` }} />
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="mono">No picks</span>
                      )}
                    </td>
                    <td className="mono" data-testid="all-teams-gates">
                      {cell(detail?.jobs, gatesHeldWords)}
                    </td>
                    <td className="mono" data-testid="all-teams-review">
                      {cell(detail?.jobs, reviewWords)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="fact">
        Pull request reviews on repositories outside any team are listed under Pull request reviews. Teams own their
        repositories' reviews once they have them.
      </p>
    </div>
  );
}
